import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AuthUser {
  id?: string;
  email: string;
  name: string;
  picture?: string;
  access_token?: string;
  user_metadata?: {
    full_name?: string;
    avatar_url?: string;
    bio?: string;
    location?: string;
  };
}

interface LoginCredentials {
  email: string;
  password: string;
  name?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signInWithGoogle: (redirectUrl?: string) => Promise<void>;
  signInWithFacebook: (redirectUrl?: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refreshSession: () => Promise<boolean>;
  validateSession: () => Promise<boolean>;
}

const DEFAULT_AVATAR_URL = 'https://vhaizqhkodqyqplpqcss.supabase.co/storage/v1/object/public/avatars/default-avatar.png';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing Supabase session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Error getting session:', error);
        
        // Handle specific error types
        if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
          console.error('406 error in AuthContext - authentication/header issue:', error);
          // Don't throw error, just set as unauthenticated
        } else if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          console.warn('Unauthorized session, setting as unauthenticated');
        } else {
          console.error('Unexpected session error:', error);
        }
        
        // Set as unauthenticated regardless of error type
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      if (session) {
        const userData: AuthUser = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata.full_name || session.user.email?.split('@')[0] || '',
          picture: session.user.user_metadata.avatar_url || DEFAULT_AVATAR_URL,
          access_token: session.access_token,
          user_metadata: {
            full_name: session.user.user_metadata.full_name,
            avatar_url: session.user.user_metadata.avatar_url,
            bio: session.user.user_metadata.bio,
            location: session.user.user_metadata.location,
          }
        };
        setUser(userData);
        setIsAuthenticated(true);
        setIsLoading(false);
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    }).catch((error) => {
      console.error('Unexpected error in getSession:', error);
      
      // Handle different error types
      if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
        console.error('406 error in AuthContext catch - authentication/header issue:', error);
      }
      
      // Set as unauthenticated on any error
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    });

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      try {
        if (session) {
          const userData: AuthUser = {
            id: session.user.id,
            email: session.user.email || '',
            name: session.user.user_metadata.full_name || session.user.email?.split('@')[0] || '',
            picture: session.user.user_metadata.avatar_url || DEFAULT_AVATAR_URL,
            access_token: session.access_token,
            user_metadata: {
              full_name: session.user.user_metadata.full_name,
              avatar_url: session.user.user_metadata.avatar_url,
              bio: session.user.user_metadata.bio,
              location: session.user.user_metadata.location,
            }
          };
          setUser(userData);
          setIsAuthenticated(true);
          setIsLoading(false);
        } else {
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error in onAuthStateChange:', error);
        
        // Handle different error types
        if (error instanceof Error) {
          if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
            console.error('406 error in onAuthStateChange - authentication/header issue:', error);
          }
        }
        
        // Set as unauthenticated on any error
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Add effect to handle auth-required events
  useEffect(() => {
    const handleAuthRequired = (event: CustomEvent) => {
      if (!isAuthenticated) {
        // Store the action and data to be executed after auth
        localStorage.setItem('auth_pending_action', JSON.stringify(event.detail));
      }
    };

    window.addEventListener('auth-required', handleAuthRequired as EventListener);
    return () => window.removeEventListener('auth-required', handleAuthRequired as EventListener);
  }, [isAuthenticated]);

  // Add effect to handle pending actions after authentication
  useEffect(() => {
    if (isAuthenticated) {
      const pendingAction = localStorage.getItem('auth_pending_action');
      if (pendingAction) {
        const { action, ...data } = JSON.parse(pendingAction);
        // Execute the pending action
        if (action === 'favorite') {
          // Dispatch an event to trigger the favorite action
          window.dispatchEvent(new CustomEvent('execute-favorite', { detail: data }));
        }
        localStorage.removeItem('auth_pending_action');
      }
    }
  }, [isAuthenticated]);

  const login = async (credentials: LoginCredentials) => {
    try {
      let { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) throw error;

      if (data.user) {
        const userData: AuthUser = {
          id: data.user.id,
          email: data.user.email || '',
          name: data.user.user_metadata.full_name || data.user.email?.split('@')[0] || '',
          picture: data.user.user_metadata.avatar_url || DEFAULT_AVATAR_URL,
          access_token: data.session?.access_token,
          user_metadata: {
            full_name: data.user.user_metadata.full_name,
            avatar_url: data.user.user_metadata.avatar_url,
            bio: data.user.user_metadata.bio,
            location: data.user.user_metadata.location,
          }
        };
        setUser(userData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            avatar_url: DEFAULT_AVATAR_URL,
          },
        },
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Signup error:', error);
      return { data: null, error };
    }
  };

  const signInWithGoogle = async (redirectUrl?: string) => {
    try {
      const callbackUrl = redirectUrl 
        ? `${import.meta.env.VITE_APP_URL}/auth/callback?redirect=${encodeURIComponent(redirectUrl)}`
        : `${import.meta.env.VITE_APP_URL}/auth/callback`;
        
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: callbackUrl,
        },
      });

      if (error) throw error;
    } catch (error) {
      console.error('Google sign in error:', error);
      throw error;
    }
  };

  const signInWithFacebook = async (redirectUrl?: string) => {
    try {
      const callbackUrl = redirectUrl 
        ? `${import.meta.env.VITE_APP_URL}/auth/callback?redirect=${encodeURIComponent(redirectUrl)}`
        : `${import.meta.env.VITE_APP_URL}/auth/callback`;
        
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: callbackUrl,
          scopes: 'email',
        },
      });

      if (error) throw error;
    } catch (error) {
      console.error('Facebook sign in error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const getAccessToken = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting access token:', error);
        
        if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
          console.error('406 error in getAccessToken - authentication/header issue:', error);
        }
        
        return null;
      }
      
      return session?.access_token || null;
    } catch (error) {
      console.error('Unexpected error in getAccessToken:', error);
      return null;
    }
  };

  const refreshSession = async () => {
    try {
      // Try to refresh the session with retry logic
      let attempts = 0;
      const maxAttempts = 3;
      let lastError;

      while (attempts < maxAttempts) {
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            lastError = error;
            console.error(`Session refresh attempt ${attempts + 1} failed:`, error);
            
            if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
              console.error('406 error in refreshSession - authentication/header issue:', error);
            }
            
            // If it's a network error, wait and retry
            if (error.message?.includes('network') || error.message?.includes('fetch') || error.message?.includes('ERR_NETWORK_CHANGED')) {
              attempts++;
              if (attempts < maxAttempts) {
                console.log(`Retrying session refresh in 1 second... (attempt ${attempts}/${maxAttempts})`);
                await new Promise(resolve => setTimeout(resolve, 1000));
                continue;
              }
            }
            
            // For other errors, don't retry
            break;
          }
          
          if (session) {
            const userData: AuthUser = {
              id: session.user.id,
              email: session.user.email || '',
              name: session.user.user_metadata.full_name || session.user.email?.split('@')[0] || '',
              picture: session.user.user_metadata.avatar_url || DEFAULT_AVATAR_URL,
              access_token: session.access_token,
              user_metadata: {
                full_name: session.user.user_metadata.full_name,
                avatar_url: session.user.user_metadata.avatar_url,
                bio: session.user.user_metadata.bio,
                location: session.user.user_metadata.location,
              }
            };
            setUser(userData);
            setIsAuthenticated(true);
            return true;
          } else {
            setUser(null);
            setIsAuthenticated(false);
            return false;
          }
        } catch (networkError) {
          lastError = networkError;
          console.error(`Session refresh attempt ${attempts + 1} failed with network error:`, networkError);
          attempts++;
          
          if (attempts < maxAttempts) {
            console.log(`Retrying session refresh in 1 second... (attempt ${attempts}/${maxAttempts})`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      
      // All attempts failed
      console.error('All session refresh attempts failed. Last error:', lastError);
      setUser(null);
      setIsAuthenticated(false);
      return false;
      
    } catch (error) {
      console.error('Unexpected error in refreshSession:', error);
      
      if (error instanceof Error) {
        if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
          console.error('406 error in refreshSession catch - authentication/header issue:', error);
        }
      }
      
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  };

  const validateSession = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        console.log('Session validation failed, attempting refresh...');
        return await refreshSession();
      }
      
      // Check if session is expired or about to expire (within 5 minutes)
      const expiresAt = session.expires_at;
      const now = Math.floor(Date.now() / 1000);
      const expiresInSeconds = expiresAt - now;
      
      if (expiresInSeconds < 300) { // Less than 5 minutes
        console.log('Session expires soon, refreshing...');
        return await refreshSession();
      }
      
      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, signUp, signInWithGoogle, signInWithFacebook, logout, getAccessToken, refreshSession, validateSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
